function timestampMs(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 0;
  }
  return numeric < 100000000000 ? Math.trunc(numeric * 1000) : Math.trunc(numeric);
}

function firstString(...values) {
  const value = values.find(item => item !== undefined && item !== null && String(item).trim());
  return value === undefined ? '' : String(value).trim();
}

export function groupReceiptMessageIdentity(conversation, message) {
  const raw = message?.raw || {};
  const groupID = firstString(raw.groupID, conversation?.target, conversation?.raw?.groupID);
  const clientMsgID = firstString(raw.clientMsgID);
  const serverMsgID = firstString(raw.serverMsgID, message?.messageUid);
  const senderID = firstString(raw.sendID, message?.from);
  if (!groupID || (!clientMsgID && !serverMsgID) || !senderID) {
    return null;
  }
  return {
    conversationID: `group-${groupID}`,
    clientMsgID,
    serverMsgID,
    groupID,
    senderID,
    sentAt: timestampMs(raw.sendTime || raw.createTime || message?.displayTimestamp || message?.timestamp)
  };
}

function receiptTime(receipt, key) {
  return timestampMs(receipt?.[key] ?? receipt?.[key === 'readAt' ? 'read_at_ms' : 'delivered_at_ms']);
}

function memberJoinTime(member) {
  return timestampMs(member?.raw?.joinTime || member?.joinTime);
}

function memberView(member, time = 0) {
  return {
    uid: firstString(member?.uid, member?.id, member?.userID),
    displayName: firstString(member?.displayName, member?.nickname, member?.name, member?.uid, member?.id),
    nickname: firstString(member?.nickname, member?.displayName, member?.name),
    portrait: firstString(member?.portrait, member?.faceURL),
    role: firstString(member?.role),
    time
  };
}

function sortMembers(left, right) {
  const roleRank = { owner: 0, admin: 1, member: 2 };
  const roleDifference = (roleRank[left.role] ?? 3) - (roleRank[right.role] ?? 3);
  if (roleDifference) {
    return roleDifference;
  }
  return String(left.displayName || left.uid).localeCompare(String(right.displayName || right.uid), 'zh-CN');
}

export function classifyGroupMessageReceipts({ members = [], receipts = [], senderID = '', sentAt = 0 } = {}) {
  const sentAtMs = timestampMs(sentAt);
  const receiptByUser = new Map(
    (receipts || []).map(receipt => [firstString(receipt?.userID, receipt?.uid, receipt?.user_id), receipt])
  );
  const eligibleMembers = (members || []).filter(member => {
    const uid = firstString(member?.uid, member?.id, member?.userID);
    if (!uid || uid === String(senderID || '')) {
      return false;
    }
    const joinedAt = memberJoinTime(member);
    return !sentAtMs || !joinedAt || joinedAt <= sentAtMs;
  });

  const group = {
    read: [],
    unread: [],
    undelivered: []
  };
  eligibleMembers.forEach(member => {
    const uid = firstString(member?.uid, member?.id, member?.userID);
    const receipt = receiptByUser.get(uid);
    const readAt = receiptTime(receipt, 'readAt');
    const deliveredAt = receiptTime(receipt, 'deliveredAt');
    if (readAt) {
      group.read.push(memberView(member, readAt));
    } else if (deliveredAt) {
      group.unread.push(memberView(member, deliveredAt));
    } else {
      group.undelivered.push(memberView(member));
    }
  });
  group.read.sort(sortMembers);
  group.unread.sort(sortMembers);
  group.undelivered.sort(sortMembers);
  group.counts = {
    read: group.read.length,
    unread: group.unread.length,
    undelivered: group.undelivered.length,
    total: eligibleMembers.length
  };
  return group;
}

export { timestampMs as normalizeReceiptTimestamp };
